import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { Tooltip } from "react-tooltip";
import { useNavigate, useLocation } from "react-router-dom";
import { Column } from "primereact/column";
import { DataTable, DataTableStateEvent, SortOrder } from "primereact/datatable";
import { CheckCircleIcon, NoSymbolIcon, PencilIcon } from "@heroicons/react/24/outline";
import { useAppSelector } from "../../../app/hooks";
import { ClassLinkOrCleverIconWithTooltip } from "../../../components/ClassLinkOrCleverIcon";
import SearchFilter from "../../../components/input/SearchFilter";
import Select from "../../../components/input/Select";
import SimplePagination from "../../../components/SimplePagination";
import { CustomSortIcon } from "../../../components/DatatableHelpers";
import { SchoolFilter } from "../../schools/schoolsApiSlice";
import { UserSchoolViewModel } from "../usersApiSlice";
import AddSchoolToUserButton from "./AddSchoolToUserButton";
import RemoveUserFromSchool from "./RemoveUserFromSchool";

interface UserSchoolTableModel extends UserSchoolViewModel {
	isRowSelected: boolean;
}
interface UserSchoolsProps {
	schools: UserSchoolViewModel[];
	showDistrictColumn: boolean;
	userId: number;
	userName: string;
	showDisenrollmentMessage: boolean;
	canRemoveSchool: boolean;
	canAddSchool: boolean;
}

export default function UserSchoolsSection(props: UserSchoolsProps) {
	const _displayLength = 3;
	const { config } = useAppSelector((state) => state.site);
	const hasClassLinkOrCleverSchool = props.schools?.some(
		(s) => s.isClassLinkSchool || s.isCleverSchool
	);

	const [page, setPage] = useState(0);
	const [searchFilter, setSearchFilter] = useState("");
	const [statusFilter, setStatusFilter] = useState("All");
	const [isFiltered, setIsFiltered] = useState(false);
	const [sortField, setSortField] = useState<string>();
	const [sortOrder, setSortOrder] = useState<SortOrder>(0);

	const [selectedSchools, setSelectedSchools] = useState<UserSchoolViewModel[]>([]);
	const [isRemovingSchool, setIsRemovingSchool] = useState(false);

	const navigate = useNavigate();
	const location = useLocation();

	function resetTable() {
		setSelectedSchools([]);
		setIsRemovingSchool(false);
		setPage(0);
	}

	const filteredSchools = useMemo(() => {
		return (
			props.schools.filter((school) => {
				if (searchFilter) {
					const searchLower = searchFilter.toLowerCase();
					const matchesSearch =
						school.name.toLowerCase().includes(searchLower) ||
						school.externalID?.toLowerCase().includes(searchLower);
					if (!matchesSearch) return false;
				}

				if (statusFilter === "Active" && !school.active) return false;
				if (statusFilter === "Inactive" && school.active) return false;

				return true;
			}) ?? []
		);
	}, [props.schools, searchFilter, statusFilter]);

	const sortedSchools = useMemo(() => {
		if (!sortField || sortOrder === 0 || !sortOrder) {
			return filteredSchools;
		}

		const sorted = [...filteredSchools].sort((a, b) => {
			const normalize = (val: string | null | undefined) => val ?? "";

			// Special handling for Primary Contact sorting: sort by name first, then by phone as tiebreaker
			if (sortField === "contact") {
				const aName = normalize(a.contactName);
				const bName = normalize(b.contactName);
				if (aName !== bName) {
					return aName.localeCompare(bName) * sortOrder;
				}
				const aPhone = normalize(a.phone);
				const bPhone = normalize(b.phone);
				return aPhone.localeCompare(bPhone) * sortOrder;
			}

			// Special handling for Address sorting: sort by address first, then by city as tiebreaker
			if (sortField === "address") {
				const aAddress = normalize(a.address1 + ", " + a.address2);
				const bAddress = normalize(b.address1 + ", " + b.address2);
				if (aAddress !== bAddress) {
					return aAddress.localeCompare(bAddress) * sortOrder;
				}
				const aCity = normalize(a.city);
				const bCity = normalize(b.city);
				return aCity.localeCompare(bCity) * sortOrder;
			}

			let aValue = normalize(a[sortField as keyof UserSchoolViewModel] as string);
			let bValue = normalize(b[sortField as keyof UserSchoolViewModel] as string);

			// Push empty strings to the end
			if (aValue === "" && bValue === "") return 0;
			if (aValue === "") return 1;
			if (bValue === "") return -1;

			return (
				aValue.localeCompare(bValue, undefined, {
					numeric: true, // Allow numbers to be sorted in a numerical order
					sensitivity: "base",
				}) * sortOrder
			);
		});

		return sorted;
	}, [filteredSchools, sortField, sortOrder]);

	const onSort = (event: DataTableStateEvent) => {
		setSortField(event.sortField);
		setSortOrder(event.sortOrder ?? 0);
		setPage(0);
	};

	const paginatedSchools = sortedSchools.slice(
		page * _displayLength,
		(page + 1) * _displayLength
	);

	const handlePageChange = (newPage: number) => {
		setPage(newPage);
	};

	useEffect(() => {
		setIsFiltered(!!searchFilter || statusFilter !== "All");
		setPage(0);
	}, [searchFilter, statusFilter]);

	const checkboxBodyTemplate = (row: UserSchoolTableModel) => {
		if (!row.canEditSchool) return;

		return (
			<input
				type="checkbox"
				value={row.schoolID}
				checked={row.isRowSelected}
				onChange={handleSelectOne}
				className="cursor-pointer size-4 rounded-sm border-gray-300 text-primary-600 focus:ring-primary-600"
				aria-label={`Select school: ${row.name}`}
			/>
		);
	};

	const activeBodyTemplate = (row: UserSchoolTableModel) => {
		return (
			<span className="flex justify-center">
				{row.active ? (
					<CheckCircleIcon
						className="size-6 text-green-500"
						data-tooltip-id={`enabled-${row.schoolID}-tooltip`}
					/>
				) : (
					<NoSymbolIcon
						className="size-6 text-red-500"
						data-tooltip-id={`enabled-${row.schoolID}-tooltip`}
					/>
				)}
				<Tooltip
					id={`enabled-${row.schoolID}-tooltip`}
					place="top-start"
					content={`${row.active ? "Active" : "Inactive"}`}
				/>
			</span>
		);
	};

	const cleverOrClassLinkBodyTemplate = (row: UserSchoolTableModel) => {
		const tooltipContent = row.isCleverSchool
			? "This school has a Clever account"
			: row.isClassLinkSchool
				? "This school has a ClassLink account"
				: undefined;

		return (
			<ClassLinkOrCleverIconWithTooltip
				isClassLinkEntity={!!row.isClassLinkSchool}
				isCleverEntity={!!row.isCleverSchool}
				tooltipId={`school-${row.schoolID}-outside-tooltip`}
				tooltipContent={tooltipContent}
			/>
		);
	};

	const addressBodyTemplate = (row: UserSchoolTableModel) => {
		return (
			<span>
				<span>{row.address2 ? `${row.address1}, ${row.address2}` : row.address1}</span>
				<div className="mt-1 text-gray-500 text-sm">{row.city}</div>
			</span>
		);
	};

	const contactBodyTemplate = (row: UserSchoolTableModel) => {
		return (
			<span>
				<span>{row.contactName}</span>
				<div className="mt-1 text-gray-500 text-sm">{row.phone}</div>
			</span>
		);
	};

	const editBodyTemplate = (row: UserSchoolTableModel) => {
		if (!row.canEditSchool) return;
		return (
			<button
				id={`edit-${row.schoolID}-button`}
				className="mr-2 cursor-pointer"
				onClick={() => {
					const returnTo = location.pathname + location.search;
					navigate(`/schools/edit?schoolId=${row.schoolID}`, {
						state: {
							returnTo: returnTo,
						},
					});
				}}>
				<PencilIcon className="size-5" />
			</button>
		);
	};

	const handleSelectOne = (e: ChangeEvent<HTMLInputElement>) => {
		const schoolId = e.currentTarget.value;
		const isChecked = e.currentTarget.checked;

		const school = props.schools.find((s) => String(s.schoolID) === schoolId);
		if (!school) return;

		if (isChecked) {
			setSelectedSchools((prev) => [...prev, school]);
		} else {
			setSelectedSchools((prev) => prev.filter((s) => s.schoolID !== school.schoolID));
		}
	};

	const handleSelectAll = (isChecked: boolean) => {
		if (isChecked) {
			// Add any missing editable schools from filteredSchools to selectedSchools
			setSelectedSchools((prev) => {
				const newSchools = filteredSchools.filter(
					(fs) => fs.canEditSchool && !prev.some((ss) => ss.schoolID === fs.schoolID)
				);
				return [...prev, ...newSchools];
			});
		} else {
			// Remove all filteredSchools from selectedSchools
			setSelectedSchools((prev) =>
				prev.filter((ss) => !filteredSchools.some((fs) => fs.schoolID === ss.schoolID))
			);
		}
	};

	const allChecked = useMemo(() => {
		const editableSchools = filteredSchools.filter((s) => s.canEditSchool);
		return (
			editableSchools.length > 0 &&
			editableSchools.every((s) => selectedSchools.some((ss) => ss.schoolID === s.schoolID))
		);
	}, [filteredSchools, selectedSchools]);

	const schoolsWithSelection: UserSchoolTableModel[] = useMemo(() => {
		return paginatedSchools.map((school) => ({
			...school,
			isRowSelected: selectedSchools.some((s) => s.schoolID === school.schoolID),
		}));
	}, [paginatedSchools, selectedSchools]);

	return (
		<section>
			<hr className="my-6" />
			<h2>Schools</h2>
			<div className="flex gap-3 mt-4 items-end justify-between">
				<div className="flex gap-3">
					{!config?.isUtahComposeSite && (
						<Select
							value={statusFilter}
							setValue={(v) => setStatusFilter(v as string)}
							label="Status"
							simpleListBoxId="schoolStatusFilter"
							options={[
								{
									value: "All",
									label: SchoolFilter.All,
								},
								{
									value: "Active",
									label: SchoolFilter.Active,
								},
								{
									value: "Inactive",
									label: SchoolFilter.Inactive,
								},
							]}
							fieldClasses="w-56"
						/>
					)}
					<SearchFilter
						id="schoolSearch"
						name="schoolSearch"
						searchFilter={searchFilter}
						setSearchFilter={setSearchFilter}
						label="Search"
						placeHolder="Search by school name or external ID..."
						className="w-96"
					/>
				</div>

				<div className="flex gap-x-3 items-end">
					{props.canAddSchool && (
						<AddSchoolToUserButton
							userId={props.userId}
							schoolIds={props.schools.map((d) => d.schoolID)}
						/>
					)}
					{props.canRemoveSchool && (
						<RemoveUserFromSchool
							selectedSchools={selectedSchools}
							isLoading={isRemovingSchool}
							name={props.userName}
							userId={props.userId}
							showDisenrollmentMessage={props.showDisenrollmentMessage}
							resetTable={resetTable}
							setIsLoading={setIsRemovingSchool}
						/>
					)}
				</div>
			</div>
			<DataTable
				value={schoolsWithSelection}
				loading={isRemovingSchool}
				dataKey="schoolID"
				emptyMessage="No schools found"
				stripedRows
				className="tbl tbl-data tbl-datatable min-w-[40rem] cursor-default"
				style={{ marginTop: "16px" }}
				sortField={sortField}
				sortOrder={sortOrder}
				onSort={onSort}
				sortIcon={CustomSortIcon}>
				{props.canRemoveSchool && (
					<Column
						columnKey="school-checkbox"
						header={
							<input
								type="checkbox"
								checked={allChecked}
								onChange={(e) => handleSelectAll(e.target.checked)}
								className="cursor-pointer size-4 rounded-sm border-gray-300 text-primary-600 focus:ring-primary-600"
								aria-label="Select all schools"
							/>
						}
						body={checkboxBodyTemplate}
						className="w-12"
						headerClassName="text-center"
						bodyClassName="text-center"
					/>
				)}
				<Column
					header="Active"
					body={activeBodyTemplate}
					headerClassName="!w-12"
					className="!w-12 !text-center"
				/>
				{!config?.isUtahComposeSite && hasClassLinkOrCleverSchool && (
					<Column
						body={cleverOrClassLinkBodyTemplate}
						headerClassName="!px-0 w-4"
						className="!px-0"></Column>
				)}
				<Column header="Name" field="name" sortable></Column>
				<Column header="External ID" field="externalID" sortable></Column>
				<Column
					header="Primary Contact"
					body={contactBodyTemplate}
					sortable
					sortField="contact"
				/>
				<Column
					header="Address"
					body={addressBodyTemplate}
					sortable
					sortField="address"></Column>
				{props.showDistrictColumn && (
					<Column header="District" field="districtName" sortable></Column>
				)}
				<Column body={editBodyTemplate} className="w-4 !pl-1 !pr-2"></Column>
			</DataTable>
			<SimplePagination
				itemCount={sortedSchools?.length}
				page={page}
				pageSize={_displayLength}
				isFiltered={isFiltered}
				totalCount={props.schools?.length}
				onChange={(page) => handlePageChange(page)}
				className={"border-none"}
			/>
		</section>
	);
}
